import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Coins, LogOut, LayoutDashboard, Crown, UserCircle2, Upload } from "lucide-react";
import { Button } from "./ui/button";
import UpgradeDialog from "./UpgradeDialog";

export function Logo({ to = "/" }) {
    return (
        <Link to={to} className="flex items-center gap-2 group" data-testid="logo-link">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/30 transition-transform group-hover:-rotate-6">
                <Coins className="h-5 w-5" />
            </span>
            <span className="font-heading text-xl font-extrabold tracking-tight text-foreground">
                Sift<span className="text-primary">.</span>
            </span>
        </Link>
    );
}

export default function Navbar() {
    const { user, logout, isPro } = useAuth();
    const navigate = useNavigate();
    const [upgradeOpen, setUpgradeOpen] = useState(false);

    const handleLogout = async () => { await logout(); navigate("/"); };

    return (
        <nav className="sticky top-0 z-50 glass border-b border-border" data-testid="navbar">
            <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />
            <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
                <Logo />
                <div className="flex items-center gap-1.5 sm:gap-3">
                    {user ? (
                        <>
                            {isPro ? (
                                <Link to="/account" data-testid="nav-pro-badge" className="hidden sm:inline-flex items-center gap-1 rounded-full border border-accent/30 bg-accent/15 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-accent hover:bg-accent/25">
                                    <Crown className="h-3 w-3" /> Pro
                                </Link>
                            ) : (
                                <Button variant="ghost" size="sm" onClick={() => setUpgradeOpen(true)} data-testid="nav-go-pro-btn" className="text-accent hover:text-accent hover:bg-accent/10">
                                    <Crown className="h-4 w-4 mr-1.5" /> Go Pro
                                </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-btn" className="hidden sm:inline-flex text-muted-foreground hover:text-foreground">
                                <LayoutDashboard className="h-4 w-4 mr-1.5" /> Dashboard
                            </Button>
                            <Button size="sm" onClick={() => navigate("/upload")} data-testid="nav-upload-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                                <Upload className="h-4 w-4 mr-1.5" /> <span className="hidden sm:inline">Add statement</span><span className="sm:hidden">Upload</span>
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => navigate("/account")} data-testid="nav-account-btn" className="text-muted-foreground hover:text-foreground" title="Account & plan">
                                <UserCircle2 className="h-5 w-5" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="nav-logout-btn" className="text-muted-foreground hover:text-foreground" title="Sign out">
                                <LogOut className="h-4 w-4" />
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="ghost" size="sm" asChild data-testid="nav-pricing-link" className="text-muted-foreground hover:text-foreground">
                                <Link to="/#pricing">Pricing</Link>
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="nav-login-btn" className="text-muted-foreground hover:text-foreground">Sign in</Button>
                            <Button size="sm" onClick={() => navigate("/register")} data-testid="nav-register-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Get started free</Button>
                        </>
                    )}
                </div>
            </div>
        </nav>
    );
}
